/**
 * What happens to a payment request after it has been raised.
 *
 *   `POST /api/v1/payment-requests/{public_id}/confirm/` — the money is in
 *   `POST /api/v1/payment-requests/{public_id}/cancel/`  — call it off
 *
 * Neither belongs to the clients module or to the enquiries one: a request is
 * created under whichever record it was asked of, but from then on it is a
 * record in its own right, keyed by its own `public_id`. Both screens that can
 * settle one come through here, so there is a single description of what each
 * call sends and answers with.
 *
 * `confirm` is the one call in the app that exists because the backend cannot
 * know something: no gateway reports a UPI transfer or an envelope of cash, so
 * the day the money arrived is the dietitian's to state, and this is where they
 * state it.
 *
 * `cancel` answers with the whole request rather than a receipt, which is why
 * it hands back the response as it arrived: the two callers hold different
 * kinds of row and each maps it through its own feature's shape. Nothing above
 * a feature service should call that one.
 */

import { api, API_ENDPOINTS, withValidationDetail } from "../../../lib/api";
import type {
  ConfirmPaymentRequestPayload,
  PaymentReceiptResponse,
  PaymentRequestResponse,
} from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import { toAmountNumber } from "../../../lib/api";
import type { PaymentReceipt } from "../types/payments.types";

const token = async (getToken: GetToken, what: string): Promise<string> => {
  const value = await getToken();
  if (!value) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot ${what}.`);
  }
  return value;
};

/**
 * Marks a request paid, on the day it was actually received.
 *
 * The date is the caller's because the money did not arrive here: it is what
 * the confirm dialog asks for, and it can be any day from the one the request
 * was raised up to today.
 *
 * A 400 arrives as an `ApiError` whose message names the field the backend
 * rejected — a date it will not take, most likely — rather than just
 * "Validation failed". A request that has already been settled answers 409,
 * which reaches the caller as its own message.
 */
export async function confirmPaymentRequest(
  getToken: GetToken,
  requestId: string,
  receivedOn: string,
  signal?: AbortSignal
): Promise<PaymentReceipt> {
  const body: ConfirmPaymentRequestPayload = { received_on: receivedOn };

  try {
    const response = await api.post<PaymentReceiptResponse>(
      API_ENDPOINTS.paymentRequests.confirm(requestId),
      body,
      { token: await token(getToken, "confirm this payment"), signal }
    );

    return {
      id: response.public_id,
      amount: toAmountNumber(response.paid_amount),
      currency: response.currency ?? "INR",
      // The backend's own record of the day, not the one that was sent: what
      // the row shows is then what was stored, however it was normalised.
      receivedOn: response.received_on,
    };
  } catch (error) {
    throw withValidationDetail(error);
  }
}

/**
 * Calls a raised request off, and hands back the request as the backend now
 * holds it — `CANCELLED`, and carrying the moment it was called off at.
 *
 * The response, untouched: see the note at the top of this file. A feature
 * service maps it; nothing above one should see it.
 */
export async function cancelPaymentRequest(
  getToken: GetToken,
  requestId: string,
  signal?: AbortSignal
): Promise<PaymentRequestResponse> {
  return api.post<PaymentRequestResponse>(
    API_ENDPOINTS.paymentRequests.cancel(requestId),
    undefined,
    { token: await token(getToken, "cancel this payment"), signal }
  );
}
